import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Volume2, VolumeX, Plus, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { Logo, Avatar } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { HeaderShortcuts } from "@/components/Shortcuts";
import { BottomNav } from "@/components/BottomNav";
import { ShortCard } from "@/components/shorts/ShortCard";
import { CommentsSheet } from "@/components/shorts/CommentsSheet";
import { FollowingEmpty } from "@/components/shorts/FollowingEmpty";

function useInfiniteShorts(focusId, following) {
  const [items, setItems] = useState([]);
  const [done, setDone] = useState(false);
  const state = useRef({ next: 0, round: 0, loading: false, ready: false });
  const loadMore = useCallback(async () => {
    const st = state.current;
    if (st.loading || (following && st.next === null)) return;
    st.loading = true;
    if (!st.ready) {
      st.ready = true;
      if (focusId) {
        try {
          const { data } = await api.get(`/shorts/${focusId}`);
          setItems([{ ...data, key: `${data.id}-focus` }]);
        } catch {
          toast.error("Vídeo não encontrado ou removido");
        }
      }
    }
    const { data } = await api.get("/shorts", { params: { skip: st.next, limit: 6, ...(following ? { following: true } : {}) } });
    const round = st.round;
    const fresh = data.items.filter((s) => round > 0 || s.id !== focusId);
    setItems((prev) => [...prev, ...fresh.map((s) => ({ ...s, key: `${s.id}-${round}-${s.slot ?? ""}` }))]);
    if (data.next === null && following) { st.next = null; setDone(true); }
    else if (data.next === null) { st.next = 0; st.round += 1; } else st.next = data.next;
    st.loading = false;
  }, [focusId, following]);
  const update = (s) => setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, ...s, key: x.key } : x)));
  const remove = (id) => setItems((prev) => prev.filter((x) => x.id !== id));
  return { items, loadMore, update, remove, done };
}

function FeedTabs({ following, setFollowing }) {
  const tab = (on, label, testid) => (
    <button onClick={() => setFollowing(on)} data-testid={testid}
      className={`relative px-1 pb-1 text-sm font-extrabold transition-colors ${following === on ? "text-white" : "text-white/55"}`}>
      {label}
      <span className={`absolute inset-x-1 -bottom-0.5 h-0.5 rounded-full bg-[hsl(var(--gold))] transition-transform ${following === on ? "scale-x-100" : "scale-x-0"}`} />
    </button>
  );
  return (
    <div className="pointer-events-auto flex items-center gap-4 drop-shadow-lg" data-testid="feed-tabs">
      {tab(false, "Para você", "feed-tab-foryou")}
      {tab(true, "Acompanhando", "feed-tab-following")}
    </div>
  );
}

function FeedList({ focusId, following, muted, reload }) {
  const { items, loadMore, update, remove, done } = useInfiniteShorts(focusId, following);
  const [commentsFor, setCommentsFor] = useState(null);
  const sentinel = useRef(null);
  useEffect(() => { loadMore(); }, [loadMore]);
  useEffect(() => {
    if (!sentinel.current) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && loadMore(), { rootMargin: "200%" });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [loadMore, items.length]);
  return (
    <>
    <div className="no-scrollbar mx-auto h-[100dvh] max-w-[460px] snap-y snap-mandatory overflow-y-scroll" data-testid={following ? "shorts-feed-following" : "shorts-feed"}>
      {items.map((s) => <ShortCard key={s.key} s={s} muted={muted} focused={s.key.endsWith("-focus")} onComments={setCommentsFor} onUpdate={update} onDeleted={remove} />)}
      {items.length === 0 && (following && done ? <FollowingEmpty onRefresh={reload} /> : <div className="grid h-[100dvh] place-items-center text-muted-foreground" data-testid="shorts-loading">Carregando curtas…</div>)}
      {following && done && items.length > 0 && <p className="grid h-40 snap-start place-items-center text-sm text-muted-foreground" data-testid="following-end">Você viu tudo de quem acompanha.</p>}
      <div ref={sentinel} className="h-1" />
    </div>
    <CommentsSheet short={commentsFor} onClose={() => setCommentsFor(null)} onAdded={() => update({ ...commentsFor, comments_count: commentsFor.comments_count + 1 })} />
    </>
  );
}

export default function ShortsFeed() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const focusId = id || params.get("v");
  const [following, setFollowing] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [muted, setMuted] = useState(true);
  const { user } = useAuth();
  return (
    <div className="relative bg-black">
      <div className="pointer-events-none fixed inset-x-0 top-0 z-30 mx-auto flex max-w-[460px] items-center justify-between p-4">
        <Link to="/" className="pointer-events-auto shrink-0 whitespace-nowrap" data-testid="shorts-home-link"><Logo /></Link>
        <div className="pointer-events-auto flex gap-2">
          <Link to="/lives" className="inline-flex items-center gap-1.5 rounded-full bg-black/50 px-3 h-9 text-xs font-bold backdrop-blur ring-1 ring-red-500/40" data-testid="shorts-lives-link"><Radio size={14} className="text-red-400 live-dot" /> Lives</Link>
          <Link to="/gravar" className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--gold))] px-3 h-9 text-xs font-extrabold text-[#0B0F17]" data-testid="shorts-record-link"><Plus size={14} /> Gravar</Link>
          <button onClick={() => setMuted(!muted)} className="grid h-9 w-9 place-items-center rounded-full bg-black/50 backdrop-blur" data-testid="shorts-mute-toggle" aria-label="Som">
            {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
          </button>
          <HeaderShortcuts dark />
          <Link to={`/perfil/${user.id}`} className="hidden md:block rounded-full ring-2 ring-white/20 transition-transform hover:scale-105" data-testid="shorts-profile-link" aria-label="Meu perfil">
            <Avatar name={user.name} src={user.avatar_url || user.logo_url} size={36} />
          </Link>
        </div>
      </div>
      <div className="pointer-events-none fixed inset-x-0 top-16 z-30 mx-auto flex max-w-[460px] justify-center">
        <FeedTabs following={following} setFollowing={setFollowing} />
      </div>
      <FeedList key={`${following}-${nonce}`} focusId={following ? null : focusId} following={following} muted={muted} reload={() => setNonce((n) => n + 1)} />
      <BottomNav />
    </div>
  );
}
